import type { TopzleGameDefinition } from "@topzle/game-sdk";
import type { BotContext, GameResult } from "@topzle/types";
import { Chess, type Move, type Square } from "chess.js";
import { z } from "zod";

export const chessStateSchema = z.object({
  fen: z.string(),
  players: z.object({ white: z.string(), black: z.string() }),
  status: z.enum(["playing", "checkmate", "draw", "resigned", "timeout"]),
  winner: z.enum(["white", "black"]).nullable(),
  reason: z.string().nullable(),
  history: z.array(
    z.object({
      san: z.string(),
      from: z.string(),
      to: z.string(),
      promotion: z.string().optional(),
      at: z.number(),
    }),
  ),
  drawOfferBy: z.string().nullable(),
  clocks: z
    .object({ white: z.number().int().nonnegative(), black: z.number().int().nonnegative() })
    .nullable(),
  initialClockSeconds: z.number().int().min(30).max(10800).nullable(),
  lastMoveAt: z.number().int().nonnegative(),
});
export const chessEventSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("move"),
    from: z.string().regex(/^[a-h][1-8]$/),
    to: z.string().regex(/^[a-h][1-8]$/),
    promotion: z.enum(["q", "r", "b", "n"]).optional(),
  }),
  z.object({ type: z.literal("resign") }),
  z.object({ type: z.literal("offer_draw") }),
  z.object({ type: z.literal("accept_draw") }),
]);
export const chessSettingsSchema = z.object({
  clockSeconds: z.number().int().min(30).max(10800).nullable().default(null),
});
export type ChessState = z.infer<typeof chessStateSchema>;
export type ChessEvent = z.infer<typeof chessEventSchema>;
export type ChessSettings = z.infer<typeof chessSettingsSchema>;

/**
 * Presentation-safe legal destinations shared by the web and native boards.
 * Keeping this beside the authoritative rules prevents either client from
 * inventing a looser set of highlights than the server will accept.
 */
export function chessLegalDestinations(fen: string, from: string) {
  const board = new Chess(fen);
  return board.moves({ square: from as Square, verbose: true }).map((move) => ({
    from: move.from,
    to: move.to,
    capture: Boolean(move.captured),
    promotion: Boolean(move.promotion),
  }));
}

function colorForPlayer(state: ChessState, playerId?: string): "white" | "black" | null {
  if (playerId === state.players.white) return "white";
  if (playerId === state.players.black) return "black";
  return null;
}

function currentPlayer(state: ChessState): string {
  return new Chess(state.fen).turn() === "w" ? state.players.white : state.players.black;
}

const pieceValues: Record<string, number> = { p: 100, n: 320, b: 330, r: 500, q: 900, k: 20_000 };

function evaluatePosition(board: Chess, botColor: "w" | "b"): number {
  if (board.isCheckmate()) return board.turn() === botColor ? -100_000 : 100_000;
  if (board.isDraw()) return 0;
  let score = 0;
  for (const [rankIndex, row] of board.board().entries()) {
    for (const [fileIndex, piece] of row.entries()) {
      if (!piece) continue;
      const value = pieceValues[piece.type] ?? 0;
      const center = 7 - (Math.abs(fileIndex - 3.5) + Math.abs(rankIndex - 3.5));
      const positional =
        piece.type === "n" || piece.type === "b" ? center * 5 : piece.type === "p" ? center * 2 : 0;
      score += piece.color === botColor ? value + positional : -(value + positional);
    }
  }
  const mobility = board.moves().length * (board.turn() === botColor ? 1 : -1);
  return score + mobility + (board.isCheck() ? (board.turn() === botColor ? -35 : 35) : 0);
}

function searchPosition(
  board: Chess,
  botColor: "w" | "b",
  depth: number,
  alpha: number,
  beta: number,
): number {
  if (depth === 0 || board.isGameOver()) return evaluatePosition(board, botColor);
  const maximizing = board.turn() === botColor;
  let best = maximizing ? -Infinity : Infinity;
  const moves = board.moves({ verbose: true }).sort((left, right) => {
    const leftValue = pieceValues[left.captured ?? ""] ?? 0;
    const rightValue = pieceValues[right.captured ?? ""] ?? 0;
    return rightValue - leftValue;
  });
  for (const move of moves) {
    const next = new Chess(board.fen());
    next.move({ from: move.from, to: move.to, promotion: move.promotion ?? "q" });
    const score = searchPosition(next, botColor, depth - 1, alpha, beta);
    if (maximizing) {
      best = Math.max(best, score);
      alpha = Math.max(alpha, best);
    } else {
      best = Math.min(best, score);
      beta = Math.min(beta, best);
    }
    if (beta <= alpha) break;
  }
  return best;
}

export function chooseChessBotMove(
  state: ChessState,
  difficulty: BotContext["difficulty"],
): { from: string; to: string; promotion?: "q" | "r" | "b" | "n" } {
  const board = new Chess(state.fen);
  const moves = board.moves({ verbose: true });
  if (!moves.length) throw new Error("No legal chess moves");
  if (difficulty === "easy") {
    const move = moves[(state.history.length * 7 + 3) % moves.length] ?? moves[0];
    if (!move) throw new Error("No legal chess move");
    return {
      from: move.from,
      to: move.to,
      ...(move.promotion ? { promotion: move.promotion as "q" | "r" | "b" | "n" } : {}),
    };
  }
  const botColor = board.turn();
  // Two plies sees the opponent's best immediate reply while keeping a move
  // comfortably inside edge-worker CPU budgets. Medium remains a fast greedy scan.
  const depth = difficulty === "hard" ? 2 : 1;
  const scored = moves.map((move) => {
    const next = new Chess(board.fen());
    next.move({ from: move.from, to: move.to, promotion: move.promotion ?? "q" });
    return {
      move,
      score: searchPosition(next, botColor, depth - 1, -Infinity, Infinity),
    };
  });
  scored.sort(
    (left, right) => right.score - left.score || left.move.san.localeCompare(right.move.san),
  );
  const selected = scored[0]?.move;
  if (!selected) throw new Error("No legal chess move");
  return {
    from: selected.from,
    to: selected.to,
    ...(selected.promotion ? { promotion: selected.promotion as "q" | "r" | "b" | "n" } : {}),
  };
}

export type ChessResearchStrategy = "aggressive" | "defensive" | "random" | "strategic";

function researchPositionScore(
  board: Chess,
  botColor: "w" | "b",
  strategy: Exclude<ChessResearchStrategy, "random">,
  move: Move,
): number {
  const base = evaluatePosition(board, botColor);
  const capturedValue = pieceValues[move.captured ?? ""] ?? 0;
  const opponentMobility = board.moves().length;
  const centerSquares = new Set([
    "c3",
    "d3",
    "e3",
    "f3",
    "c4",
    "d4",
    "e4",
    "f4",
    "c5",
    "d5",
    "e5",
    "f5",
    "c6",
    "d6",
    "e6",
    "f6",
  ]);
  let centerControl = 0;
  let developedMinorPieces = 0;
  let advancedPawns = 0;
  for (const [rankIndex, row] of board.board().entries()) {
    for (const [fileIndex, piece] of row.entries()) {
      if (!piece || piece.color !== botColor) continue;
      const square = `${String.fromCharCode(97 + fileIndex)}${8 - rankIndex}`;
      if (centerSquares.has(square)) centerControl += 1;
      if (
        (piece.type === "n" || piece.type === "b") &&
        !["b1", "c1", "f1", "g1", "b8", "c8", "f8", "g8"].includes(square)
      )
        developedMinorPieces += 1;
      if (piece.type === "p") {
        const rank = 8 - rankIndex;
        advancedPawns += botColor === "w" ? Math.max(0, rank - 2) : Math.max(0, 7 - rank);
      }
    }
  }
  if (strategy === "aggressive")
    return (
      base +
      capturedValue * 1.4 +
      (board.isCheck() ? 180 : 0) -
      opponentMobility * 1.5 +
      advancedPawns * 8
    );
  if (strategy === "defensive")
    return (
      base +
      (move.san.startsWith("O-O") ? 320 : 0) +
      capturedValue * 0.45 -
      opponentMobility * 4 +
      developedMinorPieces * 18
    );
  return base + centerControl * 38 + developedMinorPieces * 42 + advancedPawns * 9;
}

/** Shared policy selector used by both live rooms and the reproducible Topzle Lab runner. */
export function chooseChessResearchMove(
  state: ChessState,
  strategy: ChessResearchStrategy,
  randomInteger: (maximumExclusive: number) => number = () => 0,
): { from: string; to: string; promotion?: "q" | "r" | "b" | "n" } {
  const board = new Chess(state.fen);
  const moves = board.moves({ verbose: true });
  if (moves.length === 0) throw new Error("No legal chess moves");
  const selected =
    strategy === "random"
      ? (moves[randomInteger(moves.length)] ?? moves[0])
      : moves
          .map((move) => {
            const next = new Chess(board.fen());
            next.move({ from: move.from, to: move.to, promotion: move.promotion ?? "q" });
            return {
              move,
              score: researchPositionScore(next, board.turn(), strategy, move),
            };
          })
          .sort(
            (left, right) =>
              right.score - left.score || left.move.san.localeCompare(right.move.san),
          )[0]?.move;
  if (!selected) throw new Error("No legal chess move");
  return {
    from: selected.from,
    to: selected.to,
    ...(selected.promotion ? { promotion: selected.promotion as "q" | "r" | "b" | "n" } : {}),
  };
}

function timedOutChess(state: ChessState, now: number): ChessState {
  if (!state.clocks || state.status !== "playing") return state;
  const board = new Chess(state.fen);
  const active = board.turn() === "w" ? "white" : "black";
  const elapsed = Math.max(0, Math.floor((now - state.lastMoveAt) / 1000));
  if (state.clocks[active] - elapsed > 0) return state;
  return {
    ...state,
    status: "timeout",
    winner: active === "white" ? "black" : "white",
    reason: "timeout",
    clocks: { ...state.clocks, [active]: 0 },
  };
}

export const chessGame: TopzleGameDefinition<ChessState, ChessEvent, ChessSettings> = {
  id: "chess",
  version: "1.0.0",
  name: "Chess",
  shortDescription: "Complete chess rules, clocks, replay, spectators, and local engine boundary.",
  longDescription:
    "Trusted chess.js validation handles check, mates, draws, castling, en passant, and promotion; legal moves never come from an LLM.",
  category: "board",
  icon: "chess-knight",
  cover: "chess",
  minPlayers: 2,
  maxPlayers: 2,
  supportsGuests: true,
  supportsLocalPlay: true,
  supportsOnlinePlay: true,
  supportsSpectators: true,
  supportsTeams: false,
  supportsAiPlayers: true,
  supportsLocalAi: true,
  supportsCloudAi: false,
  supportsTournaments: true,
  supportsPersistence: true,
  requiredCapabilities: ["text"],
  supportedLayouts: ["bubbles", "rail", "game_fullscreen", "split", "avatar_only"],
  preferredLayout: "bubbles",
  stateSchema: chessStateSchema,
  eventSchema: chessEventSchema,
  settingsSchema: chessSettingsSchema,
  createInitialState(context, settings) {
    const [white = "white", black = "black"] = context.playerIds;
    return {
      fen: new Chess().fen(),
      players: { white, black },
      status: "playing",
      winner: null,
      reason: null,
      history: [],
      drawOfferBy: null,
      clocks:
        settings.clockSeconds === null
          ? null
          : { white: settings.clockSeconds, black: settings.clockSeconds },
      initialClockSeconds: settings.clockSeconds,
      lastMoveAt: context.now,
    };
  },
  validateEvent(state, event, context) {
    if (state.status !== "playing")
      return { valid: false, code: "game_finished", message: "The game has ended" };
    const color = colorForPlayer(state, context.actorId);
    if (!color) return { valid: false, code: "spectator", message: "Spectators cannot play" };
    if (event.type === "accept_draw")
      return {
        valid: state.drawOfferBy !== null && state.drawOfferBy !== context.actorId,
        code: "no_draw_offer",
        message: "There is no opponent draw offer",
      };
    if (event.type !== "move") return { valid: true };
    if (currentPlayer(state) !== context.actorId)
      return { valid: false, code: "not_your_turn", message: "Wait for your turn" };
    try {
      const move = new Chess(state.fen).move({
        from: event.from,
        to: event.to,
        promotion: event.promotion ?? "q",
      });
      return move
        ? { valid: true }
        : {
            valid: false,
            code: "illegal_move",
            message: "That move is not legal. Choose another move and retry.",
          };
    } catch {
      return {
        valid: false,
        code: "illegal_move",
        message: "That move is not legal. Choose another move and retry.",
      };
    }
  },
  reduceEvent(state, event, context) {
    const afterClock = timedOutChess(state, context.now);
    if (afterClock.status !== "playing") return afterClock;
    const actorColor = colorForPlayer(state, context.actorId);
    if (event.type === "resign")
      return {
        ...state,
        status: "resigned",
        winner: actorColor === "white" ? "black" : "white",
        reason: "resignation",
      };
    if (event.type === "offer_draw") return { ...state, drawOfferBy: context.actorId ?? null };
    if (event.type === "accept_draw")
      return { ...state, status: "draw", winner: null, reason: "agreement", drawOfferBy: null };
    const board = new Chess(state.fen);
    const move = board.move({ from: event.from, to: event.to, promotion: event.promotion ?? "q" });
    if (!move) return state;
    let status: ChessState["status"] = "playing";
    let winner: ChessState["winner"] = null;
    let reason: string | null = null;
    if (board.isCheckmate()) {
      status = "checkmate";
      winner = move.color === "w" ? "white" : "black";
      reason = "checkmate";
    } else if (board.isDraw()) {
      status = "draw";
      reason = board.isStalemate()
        ? "stalemate"
        : board.isThreefoldRepetition()
          ? "repetition"
          : board.isInsufficientMaterial()
            ? "insufficient_material"
            : "fifty_move_rule";
    }
    const clocks = state.clocks ? { ...state.clocks } : null;
    if (clocks && actorColor)
      clocks[actorColor] = Math.max(
        0,
        clocks[actorColor] - Math.floor((context.now - state.lastMoveAt) / 1000),
      );
    return {
      ...state,
      fen: board.fen(),
      status,
      winner,
      reason,
      drawOfferBy: null,
      clocks,
      lastMoveAt: context.now,
      history: [
        ...state.history,
        {
          san: move.san,
          from: move.from,
          to: move.to,
          ...(move.promotion ? { promotion: move.promotion } : {}),
          at: context.now,
        },
      ],
    };
  },
  getPublicState: (state) => state,
  getPrivatePlayerState: () => ({}),
  getSpectatorState: (state) => state,
  getAvailableActions(state, playerId) {
    if (state.status !== "playing" || currentPlayer(state) !== playerId) return [];
    return new Chess(state.fen).moves({ verbose: true }).map((move) => ({
      type: "move",
      from: move.from,
      to: move.to,
      ...(move.promotion ? { promotion: move.promotion } : {}),
    }));
  },
  calculateResult(state): GameResult | null {
    if (state.status === "playing") return null;
    const winnerId = state.winner ? state.players[state.winner] : null;
    return {
      status: winnerId ? "completed" : "draw",
      winnerIds: winnerId ? [winnerId] : [],
      scores: {
        [state.players.white]: winnerId === state.players.white ? 1 : 0,
        [state.players.black]: winnerId === state.players.black ? 1 : 0,
      },
      reason: state.reason ?? state.status,
    };
  },
  async createBotAction(state, botContext) {
    const policy = botContext.policy;
    const move = (["aggressive", "defensive", "strategic", "random"] as const).includes(
      policy as ChessResearchStrategy,
    )
      ? chooseChessResearchMove(
          state,
          policy as ChessResearchStrategy,
          (maximum) => Math.abs(botContext.now + state.history.length * 31) % maximum,
        )
      : chooseChessBotMove(state, botContext.difficulty);
    return { type: "move", ...move };
  },
  getNextDeadline(state) {
    if (state.status !== "playing" || !state.clocks) return null;
    const active = new Chess(state.fen).turn() === "w" ? "white" : "black";
    return state.lastMoveAt + state.clocks[active] * 1000;
  },
  handleDeadline(state, context) {
    return timedOutChess(state, context.now);
  },
  handlePlayerDeparture(state, departure, context) {
    const color =
      state.players.white === departure.playerId
        ? "white"
        : state.players.black === departure.playerId
          ? "black"
          : null;
    if (!color || !departure.replacementPlayerId) return null;
    return {
      state: {
        ...state,
        players: { ...state.players, [color]: departure.replacementPlayerId },
        drawOfferBy: state.drawOfferBy === departure.playerId ? null : state.drawOfferBy,
        lastMoveAt: context.now,
      },
      replacementPlayerId: departure.replacementPlayerId,
    };
  },
};
